import { newUid } from '@/util';
import { MarkerType } from '@xyflow/react';
import { axios } from '@/axios';
import { config } from '@/config';
import {
  CategoryChemie,
  CategoryElektriciteit,
  CategoryHydraulica,
  CategoryInformatica,
  CategoryMechanica,
  CategoryNatuurkunde,
  CategoryWiskunde,
  CategoryPneumatiek,
  CategoryVeiligheid,
  CategoryTechniek,
} from '@teo/components/icons';

export function filterIdFromArray(idArray, object) {
  if (!object) {
    return idArray;
  }

  if (idArray.includes(object.id)) {
    return [object.id];
  }

  return idArray;
}

export function findUniqueSourcesAndTargets(id, edges) {
  const result = new Set();
  const findChildren = (currentId) => {
    const children = edges.filter((edge) => edge.source === currentId);

    children.forEach((child) => {
      result.add(child.source);
      result.add(child.target);
    });

    children.forEach((child) => findChildren(child.target));
  };

  findChildren(id);
  return Array.from(result);
}

export async function nodeListTree(
  lpNodes,
  authOrg,
  handleRemoveNode,
  handleReplaceNode,
  handleMakeTheme,
  setThemeNodeSelected,
  setIsDraggingDisabled,
  allCategories,
  learningpathId
) {
  const result = await Promise.all(
    lpNodes.map(async (nds) => {
      let child = [];

      if (nds?.type === 'learningpath') {
        try {
          const response = await axios.get(
            `${config.backend}/learningpathnodedetails?learningpathId=${nds?.objectId}&organisationId=${authOrg}`
          );
          child = response.data;
        } catch (error) {
          console.error('Error fetching child nodes:', error);
        }
      }

      return {
        id: `${nds?.id}`,
        type: 'customNode',
        data: {
          learningPathId: learningpathId,
          nodeID: nds?.objectId,
          courseId: nds?.type === 'course' && nds?.objectId,
          trainingId: nds?.type === 'training' && nds?.objectId,
          coursecontentId: null,
          title: nds?.name,
          type: nds?.type,
          image: nds?.picture ? nds?.picture : '/assets/images/opleidingen.jpg',
          courseType: nds?.subType,
          publicationCourseType: null,
          description: null,
          mandatory: nds?.mandatory,
          blocking: nds?.blocking,
          notCompleted: nds?.notCompleted,
          notPassed: nds?.notPassed,
          tags: nds?.tags,
          handleRemoveNode: handleRemoveNode,
          handleReplaceNode: handleReplaceNode,
          handleMakeTheme: handleMakeTheme,
          setThemeNodeSelected: setThemeNodeSelected,
          setIsDraggingDisabled: setIsDraggingDisabled,
          allCategories: allCategories,
          child: child,
        },
        position: {
          x: nds?.x,
          y: nds?.y,
        },
      };
    })
  );

  return result;
}

export function edgeListTree(lpNodes) {
  const edgesList = [];
  lpNodes.forEach(({ id, roots, rootDescriptions }) => {
    let index = 0;
    rootDescriptions.forEach((desc) => {
      const [sourceHandle, targetHandle] = desc.split('/');

      const sourceObj = lpNodes.find((item) => item.id == roots[index]);
      const targetObj = lpNodes.find((item) => item.id == id);
      const animated =
        sourceObj && targetObj
          ? !targetObj?.mandatory
          : targetObj
            ? !targetObj?.mandatory
            : false;
      edgesList.push({
        id: newUid(10),
        type: 'smoothstep',
        source: `${roots[index]}`,
        sourceHandle,
        target: id.toString(),
        targetHandle: targetHandle || 'b',
        animated,
        markerEnd: {
          type: MarkerType.Arrow,
        },
      });
      index++;
    });
  });
  return edgesList;
}

export function cleanConection(type, node, nodes, edges, cleanList = []) {
  if (type === 'node') {
    const childNodes = edges
      ?.filter((eds) => eds?.source === node?.id)
      ?.map((nds) => nds?.target);

    const cleanConect = edges?.filter(
      (edge) => edge.source !== node.id && edge.target !== node.id
    );

    const updateNodeConect = childNodes?.map((nds) => {
      const chilNds = nodes?.find((el) => el?.id === nds);
      const rootsId = cleanConect
        ?.filter((el) => el?.target === nds)
        ?.map((el) => el?.source);

      const rootsConect = cleanConect
        ?.filter((el) => el?.target === nds)
        ?.map(
          (el) =>
            el?.sourceHandle + '/' + (el?.targetHandle ? el?.targetHandle : '')
        );
      return { node: chilNds, rootsId, rootsConect };
    });

    return updateNodeConect;
  }
  if (type === 'edge') {
    const dlEdge = edges?.find((eds) => eds.id === node);

    const targetNode = nodes?.find((nds) => nds?.id === dlEdge?.target);

    const rootsId = cleanList
      ?.filter((el) => el?.target === targetNode?.id)
      ?.map((el) => el?.source);

    const rootsConect = cleanList
      ?.filter((el) => el?.target === targetNode?.id)
      ?.map(
        (el) =>
          el?.sourceHandle + '/' + (el?.targetHandle ? el?.targetHandle : '')
      );
    return { node: targetNode, rootsId, rootsConect };
  }
}

export function createRootConect(nodeSelected, edges, createNodeId) {
  const rootsId = edges
    ?.filter((el) => el?.target === nodeSelected?.id)
    ?.map((el) => el?.source);

  const rootsConect = edges
    ?.filter((el) => el?.target === nodeSelected?.id)
    ?.map(
      (el) =>
        el?.sourceHandle + '/' + (el?.targetHandle ? el?.targetHandle : '')
    );

  return { rootsId, rootsConect };
}

export function applyCompetenceSkips(nodes) {
  return (nodes || []).map((node) =>
    node?.type === 'course' && node?.competenciesConditionsMet
      ? {
        ...node,
        layoutMandatory: node.mandatory,
        mandatory: false,
        blocking: false,
        skippedByCompetences: true,
      }
      : node
  );
}

export function sortDataLP(data) {
  const idSet = new Set(data.map((item) => item?.id));
  const rootMap = new Map();
  const inDegree = new Map();

  data?.forEach((item) => inDegree.set(item?.id, 0));

  data?.forEach((item) => {
    item?.roots?.forEach((root) => {
      if (!idSet.has(root)) return;
      if (!rootMap.has(root)) rootMap.set(root, []);
      rootMap.get(root).push(item);
      inDegree.set(item?.id, (inDegree.get(item?.id) || 0) + 1);
    });
  });

  const queue = [];
  data.forEach((item) => {
    if (inDegree.get(item?.id) === 0) queue.push(item);
  });

  const sorted = [];

  while (queue.length > 0) {
    const node = queue.shift();
    sorted.push(node);

    if (rootMap.has(node.id)) {
      rootMap.get(node.id).forEach((child) => {
        inDegree.set(child.id, inDegree.get(child.id) - 1);
        if (inDegree.get(child.id) === 0) queue.push(child);
      });
    }
  }

  return sorted;
}

export function categoriesIcon(icon) {
  switch (icon) {
    case 'chemie':
      return CategoryChemie;
    case 'elektriciteit':
      return CategoryElektriciteit;
    case 'hydraulica':
      return CategoryHydraulica;
    case 'informatica':
      return CategoryInformatica;
    case 'wiskunde':
      return CategoryWiskunde;
    case 'mechanica':
      return CategoryMechanica;
    case 'natuurkunde':
      return CategoryNatuurkunde;
    case 'pneumatiek':
      return CategoryPneumatiek;
    case 'veiligheid':
      return CategoryVeiligheid;
    default:
      return CategoryTechniek;
  }
}
